/**
 * Renders a view's `index` representation: a browsable list of module items. Each
 * item is a compressed module-page preview — compact detail (name · type ·
 * description · facts: submodules / depends-on / used-by / exposed interfaces ·
 * aspect chips) beside the module's **live subspace board** (the real
 * `<EmbeddedBoard>` projected from the shared root, a square preview at the item's
 * height). A leaf module (no subspace) shows detail full-width. Click an item →
 * open that module as a rooted board.
 *
 * The board is NOT a static picture — it's the actual board rendered small (the
 * master-plan direction: "the embed IS the real board", no substitute substrate).
 * The list is ROW-VIRTUALIZED ({@link useWindow}) so a many-thousand-module index
 * keeps only on-screen rows in the DOM; within a visible row each board is further
 * gated by {@link LazyMount}, so live boards cost O(visible), not O(total).
 *
 * Pure presentation: the container resolves each item's detail + facts + focal
 * path + open handler and passes them down; this file only lays out and paints.
 */
import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { EmbeddedBoard } from "../embed/EmbeddedBoard.js";
import { LazyMount } from "../embed/LazyMount.js";
import { useTranslation } from "../i18n";
import { isGround } from "./viewFocus.js";
import { FOCUS_GROUND_ALPHA } from "@archlang/scene/board";
import { useWindow, type WindowOpts } from "./virtualization/windowing.js";
import { filterIndexItems, type IndexItemVM } from "./indexItems.js";
import { ViewExportMenu, type ViewExportAction } from "./export/ViewExportMenu.js";
import { indexToCsv, downloadIndexPdf, downloadText } from "./export/viewExport.js";

export type { IndexItemVM };

/** Aspect chips beyond this are summarised as "+N" so a busy module stays tidy. */
const MAX_CHIPS = 4;
/** Interface names shown inline before the rest collapse to "+N". */
const MAX_IFACES = 3;
/** Gap (px) between item rows — folded into the virtualized row stride so the
 *  spacer math accounts for it (no double-counted CSS gap between rows). */
const ROW_GAP = 12;

export interface ViewIndexViewProps {
  readonly items: readonly IndexItemVM[];
  /** Total shown modules BEFORE the render cap — the "of M" in the count, so a
   *  capped index never reads as if it showed everything. */
  readonly total: number;
  /** Authored fixed column count; omitted = a single-column list. */
  readonly columns?: number;
  readonly itemWidth: number;
  readonly itemHeight: number;
  /** Open a module (click-through) — the container opens the module's dedicated
   *  page, keyed by its qualified key, labelled by its name. */
  readonly onOpen?: (moduleId: string, moduleName: string) => void;
  /** Title used for the exported PDF heading + a filename hint. */
  readonly exportTitle?: string;
  /** Test-only seam forwarded to {@link useWindow} — jsdom has no layout, so a
   *  windowing test injects deterministic virtualizer observers. */
  readonly windowTestOverrides?: WindowOpts["testOverrides"];
  /** The view's `focus` set (origin-qualified keys — the space `IndexEntry.key`
   *  lives in). Ground cards recede; focus never filters the index, so the
   *  "N of M" count is unaffected. */
  readonly focus?: ReadonlySet<string>;
}

export function ViewIndexView({
  items,
  total,
  columns,
  itemWidth,
  itemHeight,
  onOpen,
  exportTitle,
  windowTestOverrides,
  focus,
}: ViewIndexViewProps): ReactNode {
  const { t } = useTranslation();

  // Inline header search — live-filter the resolved items by name / key / type /
  // interface / aspect (pure `filterIndexItems`). Ephemeral UI state; the filtered
  // list drives BOTH the count and the virtualized rows.
  const [query, setQuery] = useState("");
  const shown = useMemo(() => filterIndexItems(items, query), [items, query]);

  // Data export of what's on screen (respects the search filter): a CSV
  // spreadsheet and a paginated PDF module directory. PDF pulls jsPDF lazily.
  const exportActions = useMemo<ViewExportAction[]>(
    () => [
      { label: t("indexExportCsv"), run: () => downloadText(indexToCsv(shown), "index.csv", "text/csv") },
      {
        label: t("indexExportPdf"),
        run: () => downloadIndexPdf(shown, "index.pdf", exportTitle ?? "Module Index"),
      },
    ],
    [shown, exportTitle, t],
  );

  // Row-virtualize so a many-thousand-module index keeps only the on-screen rows
  // in the DOM (and, via each item's LazyMount, only their boards live). A row
  // holds `cols` items; the fixed item height makes the stride uniform, so
  // `useWindow` (spacer-slice over @tanstack/react-virtual) windows cleanly.
  const cols = columns && columns > 0 ? columns : 1;
  const rowCount = Math.ceil(shown.length / cols);
  const rowStride = itemHeight + ROW_GAP;
  const scrollRef = useRef<HTMLDivElement>(null);
  const win = useWindow(scrollRef, rowCount, {
    estimateSize: rowStride,
    ...(windowTestOverrides ? { testOverrides: windowTestOverrides } : {}),
  });

  const gridTemplateColumns =
    cols > 1 ? `repeat(${cols}, minmax(0, 1fr))` : "minmax(0, 1fr)";

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="view-index">
      <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-1.5 text-xs text-slate-400">
        <span>{t("indexShowing", { shown: shown.length, total })}</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("indexSearch")}
          aria-label={t("indexSearch")}
          data-testid="view-index-search"
          className="ml-auto w-52 rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-xs text-slate-200 placeholder:text-slate-500 focus:border-slate-500 focus:outline-none"
        />
        <ViewExportMenu actions={exportActions} label={t("indexExport")} />
      </div>
      {shown.length === 0 ? (
        <div
          className="flex flex-1 items-center justify-center p-8 text-sm text-slate-500"
          data-testid="view-index-empty"
        >
          {t("indexEmpty")}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-auto p-4"
          data-testid="view-index-scroll"
        >
          <div data-min-item-width={itemWidth} data-windowed={win.windowed ? "true" : undefined}>
            {win.before > 0 ? <div aria-hidden style={{ height: win.before }} /> : null}
            {win.indices.map((r) => {
              const start = r * cols;
              const rowItems = shown.slice(start, start + cols);
              // Each row reserves the full stride; the inner grid takes the item
              // height and the remaining ROW_GAP shows as the inter-row gap.
              return (
                <div key={r} style={{ height: rowStride }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns,
                      gap: ROW_GAP,
                      height: itemHeight,
                    }}
                  >
                    {rowItems.map((it) => (
                      <IndexItem
                  key={it.key}
                  item={it}
                  height={itemHeight}
                  onOpen={onOpen}
                  focus={focus}
                />
                    ))}
                  </div>
                </div>
              );
            })}
            {win.after > 0 ? <div aria-hidden style={{ height: win.after }} /> : null}
          </div>
        </div>
      )}
    </div>
  );
}

const ITEM_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
};

/** A muted count pill in the facts row (e.g. "3 inside", "used by 5"). */
function Stat({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <span className="rounded bg-slate-800/70 px-1.5 py-0.5 text-[10px] text-slate-400 tabular-nums">
      {children}
    </span>
  );
}

function IndexItem({
  item,
  height,
  onOpen,
  focus,
}: {
  readonly item: IndexItemVM;
  readonly height: number;
  readonly onOpen?: (moduleId: string, moduleName: string) => void;
  readonly focus?: ReadonlySet<string>;
}): ReactNode {
  const { t } = useTranslation();
  // Every module opens its page — keyed by its qualified KEY (the node-universe
  // id), labelled by its name. (Was gated on the optional `#id`, absent on most
  // modules — why the whole index was dead to clicks.)
  const clickable = !!onOpen;
  const open = clickable ? () => onOpen!(item.key, item.name) : undefined;
  const extraChips = item.aspects.length - MAX_CHIPS;
  const extraIfaces = item.interfaces.length - MAX_IFACES;
  const ground = isGround(focus, item.key);
  return (
    <div
      data-testid="view-index-item"
      data-key={item.key}
      data-leaf={item.focalPath ? undefined : "true"}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={open}
      onKeyDown={
        open
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                open();
              }
            }
          : undefined
      }
      className={`rounded-lg border border-slate-800 bg-slate-900/40 transition-shadow hover:border-slate-700 hover:shadow-lg hover:shadow-black/30 ${
        clickable ? "cursor-pointer" : ""
      }`}
      style={{ ...ITEM_STYLE, height, ...(ground ? { opacity: FOCUS_GROUND_ALPHA } : {}) }}
      {...(ground ? { "data-focus": "ground" as const } : {})}
    >
      {/* Compact detail — the shared module-page preview (header · description ·
          facts · interfaces · aspects). Full-width for a leaf; left column beside
          the board. */}
      <div
        className="flex min-w-0 flex-1 flex-col gap-1.5 overflow-hidden p-3"
        data-testid={item.focalPath ? "view-index-item-detail" : "view-index-item-leaf"}
      >
        <div>
          <div className="truncate text-sm font-semibold text-slate-100" title={item.name}>
            {item.name}
          </div>
          <div className="truncate text-[11px] text-slate-400">{item.typeName}</div>
        </div>
        {item.description ? (
          <div className="line-clamp-2 text-xs leading-snug text-slate-300">{item.description}</div>
        ) : null}

        <div className="mt-auto flex flex-col gap-1.5">
          {/* Facts row — architectural stats pulled from the model + edge graph. */}
          <div className="flex flex-wrap gap-1" data-testid="view-index-item-facts">
            {item.childCount > 0 ? <Stat>{t("indexInside", { count: item.childCount })}</Stat> : null}
            {item.dependsOn > 0 ? <Stat>{t("indexDependsOn", { count: item.dependsOn })}</Stat> : null}
            {item.usedBy > 0 ? <Stat>{t("indexUsedBy", { count: item.usedBy })}</Stat> : null}
          </div>

          {/* Exposed interfaces — the surface the module offers, by name. */}
          {item.interfaces.length > 0 ? (
            <div className="truncate text-[11px] text-slate-400" title={item.interfaces.join(", ")}>
              <span className="text-slate-500">{t("indexExposes")}</span>{" "}
              {item.interfaces.slice(0, MAX_IFACES).join(", ")}
              {extraIfaces > 0 ? ` +${extraIfaces}` : ""}
            </div>
          ) : null}

          {/* Aspect chips (team / zone / …). */}
          {item.aspects.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {item.aspects.slice(0, MAX_CHIPS).map((a) => (
                <span
                  key={a.key}
                  className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300"
                  title={`${a.key}: ${a.value}`}
                >
                  <span className="text-slate-500">{a.key}:</span> {a.value}
                </span>
              ))}
              {extraChips > 0 ? (
                <span className="rounded bg-slate-800/60 px-1.5 py-0.5 text-[10px] text-slate-400">
                  +{extraChips}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Square live-board preview — container modules only. */}
      {item.focalPath ? (
        <div
          className="relative shrink-0 border-l border-slate-800"
          style={{ width: height, height }}
          data-testid="view-index-item-board"
        >
          <LazyMount style={{ position: "absolute", inset: 0 }}>
            <EmbeddedBoard focalPath={item.focalPath} />
          </LazyMount>
        </div>
      ) : null}
    </div>
  );
}
